import { z } from 'zod';

/** Einstellungen → KI. Wie ki_cfg (Text) und ig_config (Bild) der PHP-Middleware – Schlüssel aber verschlüsselt. */
export const KI_ARTEN = ['text', 'bild'] as const;
export type KiArt = (typeof KI_ARTEN)[number];

export interface KiAnbieterInfo {
  label: string;
  /** Standardmodell, wenn kein eigenes eingetragen ist; leer = Anbieter kennt kein Modell (Stability) */
  modell: string;
  /** Vorschläge für das Modellfeld, bis „Verbindung prüfen“ die Liste des Anbieters lädt */
  modelle: readonly string[];
  schluesselLabel: string;
  hinweis?: string;
  /** Modell muss angegeben werden (Replicate: Modell-Version) */
  modellPflicht?: boolean;
}

/** Text-KI für Kategorien, Produkttexte, Anreicherung und Channels */
export const KI_TEXT_ANBIETER = {
  anthropic: {
    label: 'Anthropic (Claude)',
    modell: 'claude-sonnet-5-5',
    modelle: ['claude-sonnet-5-5', 'claude-opus-5-5', 'claude-haiku-5-5', 'claude-fable-5-1'],
    schluesselLabel: 'API-Schlüssel (sk-ant-…)',
  },
  openai: { label: 'OpenAI (GPT)', modell: 'gpt-4.1', modelle: ['gpt-4.1', 'gpt-4.1-mini'], schluesselLabel: 'API-Schlüssel (sk-…)' },
  gemini: { label: 'Google Gemini', modell: 'gemini-2.5-flash', modelle: ['gemini-2.5-flash', 'gemini-2.5-pro'], schluesselLabel: 'API-Schlüssel (AIza…)' },
} as const satisfies Record<string, KiAnbieterInfo>;
export type KiTextAnbieter = keyof typeof KI_TEXT_ANBIETER;

/** Bild-KI für Freistellen, Szenen und neue Produktbilder. Anthropic erzeugt keine Bilder. */
export const KI_BILD_ANBIETER = {
  openai: {
    label: 'OpenAI (GPT Image)',
    modell: 'gpt-image-2',
    modelle: ['gpt-image-2', 'gpt-image-1'],
    schluesselLabel: 'API-Schlüssel (sk-…)',
    hinweis: 'Bearbeitet vorhandene Produktfotos anhand eines Prompts. Benötigt einen API-Account mit verifizierter Organisation und hinterlegter Zahlungsart.',
  },
  gemini: {
    label: 'Google Gemini (Bild)',
    modell: 'gemini-2.5-flash-image',
    modelle: ['gemini-2.5-flash-image'],
    schluesselLabel: 'API-Schlüssel (AIza…)',
    hinweis: 'Erzeugt und bearbeitet Produktfotos anhand eines Prompts (Gemini-Bildmodell). API-Schlüssel aus Google AI Studio.',
  },
  stability: {
    label: 'Stability AI',
    modell: '',
    modelle: [],
    schluesselLabel: 'API-Schlüssel (sk-…)',
    hinweis: 'Bietet echtes Freistellen ohne Prompt (Remove Background) sowie Bild-zu-Bild. API-Schlüssel aus platform.stability.ai.',
  },
  replicate: {
    label: 'Replicate',
    modell: '',
    modelle: [],
    schluesselLabel: 'API-Token (r8_…)',
    hinweis: 'Zugriff auf gehostete Modelle. Die Modell-Version (Hash) wird zusätzlich benötigt.',
    modellPflicht: true,
  },
} as const satisfies Record<string, KiAnbieterInfo>;
export type KiBildAnbieter = keyof typeof KI_BILD_ANBIETER;

export const kiAnbieterListe = (art: KiArt): Record<string, KiAnbieterInfo> => (art === 'text' ? KI_TEXT_ANBIETER : KI_BILD_ANBIETER);

/** Leerraum entfernen; leer oder „••…“ (Maske aus der Oberfläche) = gespeicherten Schlüssel behalten */
export const kiSchluesselEingabe = (s: string | undefined): string | null => {
  const k = (s ?? '').replace(/\s+/g, '');
  return k === '' || k.startsWith('••') ? null : k;
};

const Modell = z.string().trim().max(120).regex(/^[A-Za-z0-9._:\/@-]*$/, 'Bitte nur die Modell-ID angeben, z. B. claude-sonnet-5-5').default('');
const Schluessel = z.string().max(500).optional();

const TextSchema = z.object({
  art: z.literal('text'),
  anbieter: z.union([z.enum(Object.keys(KI_TEXT_ANBIETER) as [KiTextAnbieter, ...KiTextAnbieter[]]), z.literal('')]),
  modell: Modell,
  /** leer oder „••…“ = bisherigen Schlüssel behalten */
  schluessel: Schluessel,
  schluesselLoeschen: z.boolean().optional(),
});
const BildSchema = z.object({
  art: z.literal('bild'),
  anbieter: z.union([z.enum(Object.keys(KI_BILD_ANBIETER) as [KiBildAnbieter, ...KiBildAnbieter[]]), z.literal('')]),
  modell: Modell,
  schluessel: Schluessel,
  schluesselLoeschen: z.boolean().optional(),
});

export const KiSpeichernSchema = z.discriminatedUnion('art', [TextSchema, BildSchema]).superRefine((d, ctx) => {
  if (d.art === 'bild' && d.anbieter === 'replicate' && !d.modell) ctx.addIssue({ code: 'custom', path: ['modell'], message: 'Bitte die Modell-Version angeben' });
});
export type KiSpeichernEingabe = z.input<typeof KiSpeichernSchema>;

/** Verbindung prüfen – mit den Eingaben der Maske, ohne zu speichern */
export const KiTestSchema = z.discriminatedUnion('art', [TextSchema.omit({ schluesselLoeschen: true }), BildSchema.omit({ schluesselLoeschen: true })]);
export type KiTestEingabe = z.input<typeof KiTestSchema>;

export interface KiZugang {
  anbieter: string;
  /** eingetragenes Modell, leer = Standard des Anbieters */
  modell: string;
  /** tatsächlich genutztes Modell */
  modellWirksam: string;
  schluesselGesetzt: boolean;
  geprueftAm: string | null;
  geaendertAm: string | null;
}

export interface KiEinstellungen {
  text: KiZugang;
  bild: KiZugang;
  /** Wahl der Text-KI: eigener Schlüssel oder KI von DXL (Abrechnung nach Verbrauch) */
  dxl: import('./anreicherung').KiDxlStand;
}

export interface KiTestErgebnis {
  ok: boolean;
  /** Meldung bei Erfolg */
  info: string | null;
  /** Meldung des Anbieters bei Fehler */
  fehler: string | null;
  /** verfügbare Modelle (Text-KI), höchstens 60 */
  modelle: string[];
}
